import { useContext, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import { isExtendedTeamMembership } from "@thalamos/aspire-common";
import type {
  FormContextData,
  Section12PaymentRequestOutcomeType,
  Section12PaymentRequestSummary,
  UpdateSection12PaymentRequestStatusInput,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { formatDoctorDisplayName } from "~/pages/Section12PaymentRequests/payment-request-helpers.js";

/** API functions that can be injected for testing */
export interface AmhpPaymentRequestReviewPopupApi {
  updatePaymentRequestStatus: (
    paymentRequestId: string,
    input: UpdateSection12PaymentRequestStatusInput,
  ) => Promise<void>;
}

/**
 * Extracts a human-readable reason from a non-2xx response body. The API is not
 * uniform: most endpoints return `{ reason }`, while errors surfaced through the
 * shared handler arrive as `{ message }`. Prefer `reason`, then fall back to
 * `message`.
 */
export function extractRejectionReason(data: unknown): string | undefined {
  if (data == null || typeof data !== "object") {
    return undefined;
  }
  const body = data as { reason?: unknown; message?: unknown };
  if (typeof body.reason === "string") {
    return body.reason;
  }
  if (typeof body.message === "string") {
    return body.message;
  }
  return undefined;
}

/**
 * Thrown when the status-update call returns a non-2xx response. Because the
 * shared axios client treats any status < 500 as a resolved response (see
 * `validateStatus` in api.ts), we must inspect the status ourselves rather
 * than relying on a rejected promise.
 */
export class PaymentRequestStatusUpdateError extends Error {
  constructor(
    public readonly status: number,
    public readonly reason?: string,
  ) {
    super(reason ?? "Failed to update payment request status");
    this.name = "PaymentRequestStatusUpdateError";
  }
}

/** Default API implementation using the real API */
const defaultApi: AmhpPaymentRequestReviewPopupApi = {
  updatePaymentRequestStatus: async (paymentRequestId, input) => {
    const response = await api.section12.updatePaymentRequestStatus(
      paymentRequestId,
      input,
    );
    if (response.status < 200 || response.status >= 300) {
      throw new PaymentRequestStatusUpdateError(
        response.status,
        extractRejectionReason(response.data),
      );
    }
  },
};

/**
 * Maps a caught status-update error to a user-facing message. We surface the
 * backend's `reason` verbatim (it is written to be read by the AMHP, e.g.
 * "You are no longer the AMHP responsible for this payment request..."), and
 * fall back to a generic message otherwise.
 */
function getReviewErrorMessage(
  error: unknown,
  action: "accept" | "reject",
): string {
  if (error instanceof PaymentRequestStatusUpdateError) {
    // A 404 means the request is no longer reachable — it was removed, or the
    // user is not a member of the team it was sent to. The backend refuses the
    // read, so it reads back as "not found"; surface a neutral message rather
    // than that raw reason.
    if (error.status === 404) {
      return "This payment request is no longer available. Please refresh and try again.";
    }
    if (error.reason) {
      return error.reason;
    }
  }
  return `Failed to ${action} payment request. Please try again.`;
}

function formatAssessmentLocation(
  address: string | null,
  postcode: string | null,
): string {
  return [address, postcode].filter(Boolean).join(", ") || "Not recorded";
}

interface AmhpPaymentRequestReviewPopupProps {
  formContext: FormContextData;
  onClose: () => void;
  reloadFormContext: () => void;
  /** Optional API override for testing */
  apiOverride?: AmhpPaymentRequestReviewPopupApi;
}

export function AmhpPaymentRequestReviewPopup({
  formContext,
  onClose,
  reloadFormContext,
  apiOverride,
}: AmhpPaymentRequestReviewPopupProps) {
  const apiClient = apiOverride ?? defaultApi;
  const userContext = useContext(LoggedInUserContext);
  const activeTeamId = userContext?.user.sessionContext?.teamId;
  const activeTeamName = userContext?.user.sessionContext?.teamName;
  // Filter to only pending requests
  const pendingRequests = (formContext.section12PaymentRequests || []).filter(
    (req) => req.status === "pending",
  );

  // Build a map of doctor user ID to their work item (for no-med-rec flow detection)
  const doctorWorkItemMap = useMemo(() => {
    const map = new Map<
      string,
      { reason?: string; reasonDescription?: string }
    >();
    formContext.workItems.forEach((wi) => {
      if (
        wi.team.type === "independent" &&
        wi.status === "completed" &&
        wi.reason
      ) {
        // Map both the assigned user and the creator
        if (wi.assignedUserId) {
          map.set(wi.assignedUserId, {
            reason: wi.reason,
            reasonDescription: wi.reasonDescription,
          });
        }
        if (wi.createdByUser?.id) {
          map.set(wi.createdByUser.id, {
            reason: wi.reason,
            reasonDescription: wi.reasonDescription,
          });
        }
      }
    });
    return map;
  }, [formContext.workItems]);

  // Helper function to get outcome label for display
  const getOutcomeTypeLabel = (
    outcomeType: Section12PaymentRequestOutcomeType,
  ): string =>
    match(outcomeType)
      .with(
        "form",
        () => "Assessment attended - medical recommendation on eMHA",
      )
      .with("no-med-rec", () => "No med rec completed")
      .with(
        "completed-outside-thalamos",
        () => "Assessment attended - medical recommendation outside of eMHA",
      )
      .with("call-out-fee", () => "No assessment done (call out fee)")
      .exhaustive();

  // Helper function to get assessment info for a request
  const getAssessmentInfo = (
    request: Section12PaymentRequestSummary,
  ): {
    type: Section12PaymentRequestOutcomeType;
    label: string;
    noMedRecReason?: string;
  } => {
    // Use outcomeType directly from the request if available
    const outcomeType = request.outcomeType ?? "form"; // Default to 'form' for legacy requests

    // For no-med-rec-reason, include the reason from the request
    if (outcomeType === "no-med-rec") {
      const reason = request.noMedRecReason;
      // Fall back to work item reason if request doesn't have it stored
      const workItemInfo = doctorWorkItemMap.get(request.doctorUserId);
      const displayReason =
        reason || workItemInfo?.reasonDescription || workItemInfo?.reason;

      return {
        type: "no-med-rec",
        label: displayReason
          ? `No med rec completed - ${displayReason}`
          : "No med rec completed (with reason)",
        noMedRecReason: displayReason,
      };
    }

    // For all outcome types, use the standard label
    return {
      type: outcomeType,
      label: getOutcomeTypeLabel(outcomeType),
    };
  };

  // ID-only state: the derived request always reflects the latest pendingRequests
  // from props, so any field the doctor updates (e.g. assessmentDateTime,
  // assessmentLocation) is visible without re-opening the dialog.
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    pendingRequests.length === 1 ? (pendingRequests[0]?.id ?? null) : null,
  );
  const selectedRequest =
    pendingRequests.find((r) => r.id === selectedRequestId) ?? null;
  const [decision, setDecision] = useState<"accept" | "reject" | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [rejectedReason, setRejectedReason] = useState<string>("");
  const [decisionError, setDecisionError] = useState(false);
  const [rejectionReasonError, setRejectionReasonError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Whether the selected request was billed to a team the user belongs to.
  // Approval follows team membership (see `section12PaymentRequestPermissions`
  // on the server), so a member may action it even while operating as a
  // different team.
  const isMemberOfOwningTeam =
    selectedRequest != null &&
    userContext != null &&
    userContext.user.memberships
      .filter(isExtendedTeamMembership)
      .some((m) => m.teamId === selectedRequest.amhpTeamId);

  // True when the user is acting on a request billed to a team other than the
  // one they are currently operating as (including an organisation-context
  // session with no active team). Approval still works for a member, but the UI
  // makes clear the response is issued on behalf of the owning team.
  const isCrossTeam =
    selectedRequest != null &&
    userContext != null &&
    selectedRequest.amhpTeamId !== activeTeamId;

  // Single source for the owning team's display name so every place it appears
  // shows the same fallback when the name is unavailable.
  const owningTeamName = selectedRequest?.amhpTeamName ?? "the AMHP team";

  const handleAccept = async () => {
    if (!selectedRequest) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const input: UpdateSection12PaymentRequestStatusInput = {
        status: "accepted",
        amhpFeedback: notes.trim()
          ? {
              notes: notes.trim(),
            }
          : undefined,
      };

      await apiClient.updatePaymentRequestStatus(selectedRequest.id, input);
      renderSuccessToast({ message: "Payment request accepted" });
      reloadFormContext();

      // If there are more pending requests, reset for the next one
      const remainingRequests = pendingRequests.filter(
        (r) => r.id !== selectedRequest.id,
      );
      if (remainingRequests.length > 0) {
        setSelectedRequestId(
          remainingRequests.length === 1
            ? (remainingRequests[0]?.id ?? null)
            : null,
        );
        setDecision(null);
        setNotes("");
        setRejectedReason("");
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to accept payment request:", error);
      const message = getReviewErrorMessage(error, "accept");
      setSubmitError(message);
      renderErrorToast({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const input: UpdateSection12PaymentRequestStatusInput = {
        status: "rejected",
        rejectedReason: rejectedReason.trim(),
        amhpFeedback: notes.trim()
          ? {
              notes: notes.trim(),
            }
          : undefined,
      };

      await apiClient.updatePaymentRequestStatus(selectedRequest.id, input);
      renderSuccessToast({ message: "Payment request rejected" });
      reloadFormContext();

      // If there are more pending requests, reset for the next one
      const remainingRequests = pendingRequests.filter(
        (r) => r.id !== selectedRequest.id,
      );
      if (remainingRequests.length > 0) {
        setSelectedRequestId(
          remainingRequests.length === 1
            ? (remainingRequests[0]?.id ?? null)
            : null,
        );
        setDecision(null);
        setNotes("");
        setRejectedReason("");
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to reject payment request:", error);
      const message = getReviewErrorMessage(error, "reject");
      setSubmitError(message);
      renderErrorToast({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      data-testid="amhp-payment-request-review-popup"
    >
      <DialogTitle sx={{ py: 1.5, px: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            Review Payment Requests ({pendingRequests.length} pending)
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            color="primary"
            data-testid="close-dialog-button"
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>
      <Box sx={{ px: 2, pb: 2 }}>
        {submitError && (
          <Banner
            bannerType={BannerList.ERROR}
            title="Error"
            body={submitError}
            sx={{ mb: 2 }}
          />
        )}

        {/* List of pending requests */}
        {!selectedRequest && pendingRequests.length > 1 && (
          <>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              Select a payment request to review:
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Assessment</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRequests.map((request) => {
                    const assessmentInfo = getAssessmentInfo(request);
                    return (
                      <TableRow key={request.id}>
                        <TableCell>
                          {request.doctorName
                            ? formatDoctorDisplayName(
                                request.doctorName,
                                request.doctorTeamName,
                                request.doctorTeamType,
                              )
                            : "Unknown Doctor"}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            data-testid="assessment-type-label"
                          >
                            {assessmentInfo.label}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {dayjs(request.created).format("DD/MM/YYYY HH:mm")}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedRequestId(request.id);
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Review selected request */}
        {selectedRequest && (
          <Box>
            {pendingRequests.length > 1 && (
              <Button
                size="small"
                onClick={() => {
                  setSelectedRequestId(null);
                }}
                sx={{ mb: 2 }}
              >
                ← Back to list
              </Button>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                Payment Request Details
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 1,
                  alignItems: "baseline",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Reference:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {selectedRequest.paymentReference}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Doctor:
                </Typography>
                <Typography variant="body2">
                  {selectedRequest.doctorName
                    ? formatDoctorDisplayName(
                        selectedRequest.doctorName,
                        selectedRequest.doctorTeamName,
                        selectedRequest.doctorTeamType,
                      )
                    : "Unknown Doctor"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Assessment:
                </Typography>
                <Box>
                  {(() => {
                    const assessmentInfo = getAssessmentInfo(selectedRequest);
                    // Show a banner for non-form outcome types
                    if (assessmentInfo.type !== "form") {
                      const getOutcomeTitle = () =>
                        match(assessmentInfo.type)
                          .with("form", () => "Assessment Outcome")
                          .with(
                            "no-med-rec",
                            () => "No Medical Recommendation Completed",
                          )
                          .with(
                            "completed-outside-thalamos",
                            () =>
                              "Assessment attended - medical recommendation outside of eMHA",
                          )
                          .with(
                            "call-out-fee",
                            () => "No assessment done (call out fee)",
                          )
                          .exhaustive();

                      const getOutcomeBody = () => {
                        if (assessmentInfo.type === "no-med-rec") {
                          return "The doctor attended this assessment but did not complete a medical recommendation.";
                        }
                        if (
                          assessmentInfo.type === "completed-outside-thalamos"
                        ) {
                          return "The doctor completed the medical recommendation outside of eMHA (on paper or another system).";
                        }
                        if (assessmentInfo.type === "call-out-fee") {
                          return "The doctor was called out but no assessment took place.";
                        }
                        return assessmentInfo.label;
                      };

                      return (
                        <Banner
                          bannerType={BannerList.WARNING}
                          title={getOutcomeTitle()}
                          body={getOutcomeBody()}
                          sx={{ mt: 1 }}
                        />
                      );
                    }
                    return (
                      <Typography variant="body2">
                        {assessmentInfo.label}
                      </Typography>
                    );
                  })()}
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Date and time of assessment:
                </Typography>
                <Typography variant="body2">
                  {selectedRequest.assessmentDateTime
                    ? dayjs(selectedRequest.assessmentDateTime).format(
                        "DD/MM/YYYY HH:mm",
                      )
                    : "Not recorded"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Location of assessment:
                </Typography>
                <Typography variant="body2">
                  {formatAssessmentLocation(
                    selectedRequest.assessmentLocationAddress,
                    selectedRequest.assessmentLocationPostcode,
                  )}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  LA responsible for payment:
                </Typography>
                <Typography variant="body2">
                  {selectedRequest.latestLocalAuthorityName ??
                    selectedRequest.localAuthorityName ??
                    "Unknown"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Submitted:
                </Typography>
                <Typography variant="body2">
                  {dayjs(selectedRequest.created).format("DD/MM/YYYY HH:mm")}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Approving Team:
                </Typography>
                <Typography variant="body2">{owningTeamName}</Typography>
              </Box>
            </Paper>

            {userContext != null && !isMemberOfOwningTeam ? (
              <Banner
                bannerType={BannerList.INFO}
                title="This request belongs to another AMHP team"
                body={`This payment request was sent to ${owningTeamName} that you are not a member of. Ask the requester to cancel it and resubmit it to the correct team.`}
                sx={{ mb: 2 }}
              />
            ) : (
              <>
                {/* Decision section */}
                <FormControl
                  component="fieldset"
                  error={decisionError}
                  sx={{ mb: 2, width: "100%" }}
                >
                  <FormLabel component="legend" sx={{ mb: 1 }}>
                    Please select an action
                  </FormLabel>
                  <RadioGroup
                    value={decision ?? ""}
                    onChange={(e) => {
                      const value = e.target.value as "accept" | "reject";
                      setDecision(value);
                      setDecisionError(false);
                      setSubmitError(null);
                      // Reset the other field when switching
                      if (value === "accept") {
                        setRejectedReason("");
                        setRejectionReasonError(false);
                      } else {
                        setNotes("");
                      }
                    }}
                  >
                    <FormControlLabel
                      value="accept"
                      control={<Radio />}
                      label="Accept request"
                      disabled={isSubmitting}
                    />
                    <FormControlLabel
                      value="reject"
                      control={<Radio />}
                      label="Reject request"
                      disabled={isSubmitting}
                    />
                  </RadioGroup>
                  {decisionError && (
                    <FormHelperText>
                      Please select whether to accept or reject this request
                    </FormHelperText>
                  )}
                </FormControl>

                {/* On-behalf-of warning: the response is attributed to the
                    owning team, which is not the one the user is operating as */}
                {decision != null && isCrossTeam && (
                  <Banner
                    bannerType={BannerList.WARNING}
                    title="Responding on behalf of another team"
                    body={`${
                      activeTeamName != null
                        ? `You are currently logged into ${activeTeamName}.`
                        : "You are not currently operating as a team."
                    } Please be aware you are issuing this response on behalf of ${owningTeamName}.`}
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Progressive disclosure: feedback for accept */}
                {decision === "accept" && (
                  <TextField
                    label="Your feedback (optional)"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Progressive disclosure: reason for reject */}
                {decision === "reject" && (
                  <TextField
                    label="Reason for rejection (required)"
                    multiline
                    rows={3}
                    value={rejectedReason}
                    onChange={(e) => {
                      setRejectedReason(e.target.value);
                      if (e.target.value.trim()) {
                        setRejectionReasonError(false);
                      }
                    }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    error={rejectionReasonError}
                    helperText={
                      rejectionReasonError ? "Please provide a reason" : ""
                    }
                  />
                )}

                {/* Action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      // Validate decision is selected
                      if (!decision) {
                        setDecisionError(true);
                        return;
                      }

                      // Validate rejection reason if rejecting
                      if (decision === "reject" && !rejectedReason.trim()) {
                        setRejectionReasonError(true);
                        return;
                      }

                      if (decision === "accept") {
                        void handleAccept();
                      } else {
                        void handleReject();
                      }
                    }}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    Confirm
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
